import { Component, computed, DestroyRef, effect, inject, input, OnInit, output, ResourceRef, Signal, signal } from '@angular/core';
import {
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelDescription,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle
} from '@angular/material/expansion';
import { MatButton } from '@angular/material/button';
import { MatDatepickerToggle, MatDateRangeInput, MatDateRangePicker, MatEndDate, MatStartDate } from '@angular/material/datepicker';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ModelService, ModelSourceProfile, NewsVendorMetadataSchema } from '../../api/sphere-ai-instant-analyst/generated';
import { Router } from '@angular/router';
import { rxResource, takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Observable, of } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { IA_MARKET_SCAN } from '../../constants/ai-paths';
import { startWith, tap } from 'rxjs/operators';
import { MatTooltip } from '@angular/material/tooltip';
import {
    CHATBOT_MODE_INTELLIGENCE,
    CHATBOT_MODE_INTELLIGENCE_BASIC,
    CHATBOT_MODE_MARKET_SCAN,
    IAIntelligenceSourceCode,
    IASourceCategory
} from '../../services/chatbot.service';
import { IAFilterValues } from '../../types/filter-types';
import { FilterPanelComponent, NoMatchesComponent } from 'shared';
import { MatDivider } from '@angular/material/list';

@Component({
    selector: 'ia-chatbot-filter-panel',
    imports: [
        MatAccordion,
        MatButton,
        MatDateRangeInput,
        MatDateRangePicker,
        MatDatepickerToggle,
        MatEndDate,
        MatExpansionPanel,
        MatExpansionPanelHeader,
        MatFormField,
        MatIcon,
        MatLabel,
        MatProgressSpinner,
        MatSlideToggle,
        MatStartDate,
        MatSuffix,
        ReactiveFormsModule,
        MatTooltip,
        MatExpansionPanelDescription,
        MatExpansionPanelTitle,
        FilterPanelComponent,
        NoMatchesComponent,
        MatDivider
    ],
    templateUrl: './ia-chatbot-filter-panel.component.html',
    styleUrl: './ia-chatbot-filter-panel.component.scss'
})
export class IaChatbotFilterPanelComponent implements OnInit {
    mode = input<string>();
    allVendors = input<NewsVendorMetadataSchema[]>();
    selectedVendor = input<string>();
    filtersChanged = output<IAFilterValues>();

    destroyRef = inject(DestroyRef);
    fb = inject(FormBuilder);
    router = inject(Router);
    private aiModelService = inject(ModelService);

    filterPanelExpanded = signal(false);
    filterForm!: FormGroup;

    vendor = signal<string | null>(null);

    private readonly STORAGE_KEY = 'sphere-chat-config-filter-panel';
    readonly CHATBOT_MODE_MARKET_SCAN = CHATBOT_MODE_MARKET_SCAN;

    private _sources: ResourceRef<ModelSourceProfile[] | undefined> = rxResource({
        params: computed(() => {
            const mode = this.mode();
            const vendor = this.vendor?.();
            if (mode === CHATBOT_MODE_MARKET_SCAN) {
                return [vendor, mode];
            }
            return [null, mode];
        }),
        stream: ({ params }): Observable<ModelSourceProfile[]> => {
            const [vendorCode, mode] = params || [];
            if (!mode) {
                return of([]);
            }
            const getProfiles = (sourceCode: string, modelCode: string) =>
                this.aiModelService.getProfilesForSourceApiInstantAnalystModelModelCodeSourceSourceCodeProfilesGet({
                    sourceCode,
                    modelCode
                });
            switch (mode) {
                case CHATBOT_MODE_MARKET_SCAN:
                    return vendorCode ? getProfiles(vendorCode, IASourceCategory.Marketscan) : of([]);
                case CHATBOT_MODE_INTELLIGENCE_BASIC:
                case CHATBOT_MODE_INTELLIGENCE: {
                    const sourceCode = mode === CHATBOT_MODE_INTELLIGENCE_BASIC ? IAIntelligenceSourceCode.Basic : IAIntelligenceSourceCode.Complete;
                    return getProfiles(sourceCode, IASourceCategory.Intelligence);
                }
                default:
                    return of([]);
            }
        }
    });

    public vendorSources = computed(() => this._sources.value());
    public vendorSourcesError = computed(() => this._sources.error() as HttpErrorResponse);
    public areVendorSourcesLoading = computed(() => this._sources.isLoading());

    formReady = signal(false);
    feedSearchControl = new FormControl('');
    private feedSearchText = toSignal(this.feedSearchControl.valueChanges, { initialValue: '' });

    readonly toggleOptions: Signal<ModelSourceProfile[]> = computed(() => {
        const sources: ModelSourceProfile[] = this.vendorSources() as ModelSourceProfile[]; // TODO: fix type!
        if (!sources) {
            return [];
        }
        return sources;
    });

    readonly filteredToggleOptions: Signal<ModelSourceProfile[]> = computed(() => {
        const options = this.toggleOptions();
        const query = this.feedSearchText()?.toLowerCase().trim() || '';

        if (!query) {
            return options;
        }

        return options.filter(option => option.description?.toLowerCase().includes(query) || option.code?.toLowerCase().includes(query));
    });

    constructor() {
        effect(() => {
            const sources = this.vendorSources();
            if (sources === null || sources === undefined) {
                return;
            }

            this.initForm();
        });
    }

    ngOnInit(): void {
        this.selectedVendor() ? this.vendor.set(this.selectedVendor() as string) : this.getVendorFromUrl();
    }

    private getVendorFromUrl(): void {
        const pathSegments = this.router.url.split('#')[0].split('/');
        const vendorSegment = (() => {
            const index = pathSegments.indexOf(IA_MARKET_SCAN);
            return pathSegments[index + 1] ?? null;
        })();

        this.vendor.set(vendorSegment);
    }

    private getDefaultDateRange() {
        const endDate = new Date();
        const startDate = new Date(endDate);
        startDate.setDate(endDate.getDate() - 30);
        return { startDate, endDate };
    }

    private loadSavedFilters() {
        const raw = sessionStorage.getItem(`${this.STORAGE_KEY}-${this.mode()}`);
        if (!raw) {
            return null;
        }

        const parsed = JSON.parse(raw);
        if (parsed.dateRange) {
            parsed.dateRange.startDate = parsed.dateRange.startDate ? new Date(parsed.dateRange.startDate) : null;
            parsed.dateRange.endDate = parsed.dateRange.endDate ? new Date(parsed.dateRange.endDate) : null;
        }
        return parsed;
    }

    private saveFilters(filters: Record<string, string>): void {
        sessionStorage.setItem(`${this.STORAGE_KEY}-${this.mode()}`, JSON.stringify(filters));
    }

    private initForm(): void {
        const saved = this.loadSavedFilters();
        const { startDate, endDate } = saved?.dateRange || this.getDefaultDateRange();

        const baseControls: Record<string, FormControl | FormGroup> = {
            dateRange: this.fb.group({ startDate: [startDate], endDate: [endDate] })
        };

        for (const toggle of this.toggleOptions()) {
            baseControls[toggle.code] = this.createToggleControl(saved?.[toggle.code], toggle.has_access);
        }

        this.filterForm = this.fb.group(baseControls);

        this.filterForm.valueChanges
            .pipe(
                startWith(this.filterForm.getRawValue()),
                takeUntilDestroyed(this.destroyRef),
                tap(value => {
                    this.saveFilters(value);
                    const flags = value;
                    const accessList: string[] = [];
                    Object.entries(flags).forEach(([key, value]) => {
                        if (key !== 'dateRange' && value === true) {
                            const source = this.vendorSources()?.find(v => v.code === key);
                            if (source) {
                                accessList.push(...source.acls);
                            }
                        }
                    });
                    const data: IAFilterValues = {
                        dateRange: value.dateRange,
                        aclValues: accessList
                    };
                    this.filtersChanged.emit(data);
                })
            )
            .subscribe();
        this.formReady.set(true);
    }

    private createToggleControl(savedValue?: boolean, hasAccess = true): FormControl {
        return new FormControl({
            value: hasAccess ? (savedValue ?? true) : false,
            disabled: !hasAccess
        });
    }

    resetFilters(): void {
        const { startDate, endDate } = this.getDefaultDateRange();

        this.filterForm.get('dateRange')?.patchValue({ startDate, endDate });

        this.toggleOptions().forEach(({ code }) => {
            const control = this.filterForm.get(code);
            if (control) {
                control.setValue(!control.disabled);
            }
        });

        this.saveFilters(this.filterForm.value);
    }

    forceRangePickerValues(): void {
        const range = this.filterForm.get('dateRange') as FormGroup;
        const startDate = range.get('startDate')?.value;
        const endDate = range.get('endDate')?.value;

        if (startDate && !endDate) {
            range.patchValue({ endDate: startDate });
        }
    }
}
